import { Controller } from "@hotwired/stimulus"
import { request } from "./utils/api"
import { normalizeHexColor } from "./utils/color"

// EM-2717: Brand Studio's live preview. Every edit posts the unsaved form to
// the preview endpoint, which compiles the brand for real (then rolls it back)
// and returns the CSS each phone frame would carry after a save. That CSS
// replaces the storefront's own <style> inside the frame, so the preview shows
// exactly what saving would - including menus whose hand-edits beat the brand.
//
// The logo, the landing background and the menu header image never go to the
// server until Save: they are previewed from the chosen file through an object
// URL, which the same-origin frames can read (so an animated GIF plays straight
// away). The header image is the Global design's, so only a menu on Global
// previews it.
//
// EM-2718: a theme (look) only fills the form's fields, exactly as if the venue
// had typed them, and previews like any other edit. Nothing is saved until
// Save, and editing a field afterwards un-marks the theme because the brand no
// longer matches it.
//
// EM-2719: hand-edits win by default. "Save and override menu styling" and
// each design's "Override with brand" replace them, so both first show the menus that will
// change (by name) and only go ahead once that is confirmed. Menu names are
// written with textContent, never as HTML.
const PREVIEW_DEBOUNCE_MS = 250
const LOGO_TARGETS = {
  landing: ".public-restaurant .restaurant-logo",
  menu: ".public-menu .menu-logo"
}
const STYLE_IDS = { landing: "restaurant-style", menu: "menu-style" }
const LOGO_STYLE_ID = "brand-studio-logo-preview"
const BACKGROUND_STYLE_ID = "brand-studio-background-preview"
// Mirrors Palette#landing_page_rules: the image goes on <body>, and the box
// around the logo and buttons is cleared so it shows through.
const BACKGROUND_PAGE = ".body-background"
const BACKGROUND_BOX = ".public-restaurant .restaurant-background"
const BACKGROUND_HINTS = {
  chosen: "Previewing your new background - save to use it.",
  removed: "No image: your landing page will show your background colour."
}
const HEADER_STYLE_ID = "brand-studio-header-preview"
const HEADER_TARGET = ".public-menu .menu-header"
const HEADER_HINTS = {
  chosen: "Previewing your new header image - save to use it.",
  ownDesign: "This menu has a design of its own, so it keeps its header. Pick a menu on your Global design to preview it."
}

export default class extends Controller {
  static targets = ["form", "status", "landingFrame", "menuFrame", "menuId", "designLink", "logoInput", "logoImage", "logoEmpty", "preset",
    "backgroundInput", "backgroundImage", "backgroundEmpty", "backgroundHint", "removeBackground", "replaceBackgroundButton", "removeBackgroundButton",
    "headerInput", "headerImage", "headerEmpty", "headerHint",
    "kioskGallery", "kioskInput", "kioskHint",
    "lookNote", "menuNote", "menuNoteText", "menuNoteForm",
    "applyToAll", "confirmModal", "confirmTitle", "confirmIntro", "confirmList", "confirmButton"]

  static values = {
    previewUrl: String,
    menuPreviewUrl: String,
    clashesUrl: String,
    menuNotes: Object,
    globalMenuIds: Array,
    csrfToken: String
  }

  connect() {
    this.previewCss = {}
    this.logoUrl = null
    // null: the saved background stands; "none" or an object URL: an unsaved choice.
    this.background = null
    this.headerUrl = null
    this.kioskUrls = []
    this.requestSeq = 0
    if (this.hasMenuIdTarget) this.showMenuNote(this.menuIdTarget.value)
  }

  disconnect() {
    clearTimeout(this.previewTimer)
    if (this.logoUrl) URL.revokeObjectURL(this.logoUrl)
    this.revokeBackground()
    if (this.headerUrl) URL.revokeObjectURL(this.headerUrl)
    this.revokeKioskImages()
  }

  changed(event) {
    // Only a real edit has an event; applyPreset calls this directly. A look
    // holds no navigation, so choosing one leaves the look marked.
    if (event && event.target?.name !== "navigation_type") this.markPreset(null)
    this.setStatus("Previewing unsaved changes")
    clearTimeout(this.previewTimer)
    this.previewTimer = setTimeout(() => this.refreshPreview(), PREVIEW_DEBOUNCE_MS)
  }

  applyPreset({ params: { values }, currentTarget }) {
    Object.entries(values).forEach(([field, value]) => {
      const input = this.formTarget.elements[`restaurant_brand[${field}]`]
      if (!input) return

      // A theme without a font clears it ("Not set"), rather than leaving the
      // previous theme's font selected.
      input.value = value ?? ""
      const picker = this.picker(field)
      if (picker && value) picker.value = value
    })
    this.markPreset(currentTarget.dataset.brandStudioPresetKey)
    if (this.hasLookNoteTarget) this.lookNoteTarget.hidden = false
    this.changed()
  }

  markPreset(key) {
    this.presetTargets.forEach((preset) => {
      const current = preset.dataset.brandStudioPresetKey === key
      preset.classList.toggle("is-current", current)
      preset.setAttribute("aria-pressed", current)
    })
  }

  pickColor(event) {
    const field = event.target.dataset.brandStudioPicker
    this.hexInput(field).value = event.target.value
  }

  typeColor(event) {
    const hex = normalizeHexColor(event.target.value)
    if (hex) this.picker(event.target.dataset.brandStudioHex).value = hex
  }

  clearColor(event) {
    this.hexInput(event.currentTarget.dataset.brandStudioClear).value = ""
    this.changed(event)
  }

  chooseLogo() {
    this.logoInputTarget.click()
  }

  logoChosen() {
    const file = this.logoInputTarget.files[0]
    if (!file) return

    if (this.logoUrl) URL.revokeObjectURL(this.logoUrl)
    this.logoUrl = URL.createObjectURL(file)
    this.logoImageTarget.src = this.logoUrl
    this.logoImageTarget.classList.remove("d-none")
    this.logoEmptyTarget.classList.add("d-none")
    this.setStatus("Previewing unsaved changes")
    this.frames().forEach(([kind, frame]) => this.applyLogo(kind, frame))
  }

  chooseBackground() {
    this.backgroundInputTarget.click()
  }

  backgroundChosen() {
    const file = this.backgroundInputTarget.files[0]
    if (!file) return

    this.revokeBackground()
    this.background = URL.createObjectURL(file)
    this.removeBackgroundTarget.value = "0"
    this.showBackground(this.background, BACKGROUND_HINTS.chosen)
  }

  // Back to the plain background colour once saved. Previewed straight away.
  removeBackground() {
    this.revokeBackground()
    this.background = "none"
    this.backgroundInputTarget.value = ""
    this.removeBackgroundTarget.value = "1"
    this.showBackground(null, BACKGROUND_HINTS.removed)
    this.changed()
  }

  showBackground(url, hint) {
    this.backgroundImageTarget.src = url || ""
    this.backgroundImageTarget.classList.toggle("d-none", !url)
    this.backgroundEmptyTarget.classList.toggle("d-none", !!url)
    this.backgroundHintTarget.textContent = hint
    this.replaceBackgroundButtonTarget.classList.toggle("d-none", !url)
    this.removeBackgroundButtonTarget.classList.toggle("d-none", !url)
    this.setStatus("Previewing unsaved changes")
    if (this.hasLandingFrameTarget) this.applyBackground(this.landingFrameTarget)
  }

  revokeBackground() {
    if (this.background && this.background !== "none") URL.revokeObjectURL(this.background)
    this.background = null
  }

  chooseHeader() {
    this.headerInputTarget.click()
  }

  headerChosen() {
    const file = this.headerInputTarget.files[0]
    if (!file) return

    if (this.headerUrl) URL.revokeObjectURL(this.headerUrl)
    this.headerUrl = URL.createObjectURL(file)
    this.headerImageTarget.src = this.headerUrl
    this.headerImageTarget.classList.remove("d-none")
    this.headerEmptyTarget.classList.add("d-none")
    this.showHeaderHint()
    if (this.hasMenuFrameTarget) this.applyHeader(this.menuFrameTarget)
  }

  chooseKioskImages() {
    this.kioskInputTarget.click()
  }

  // The kiosk app shows these, not the phone frames, so they are only listed
  // here, marked new, until Save uploads them. Choosing again replaces the
  // previous choice, as a file picker does.
  kioskImagesChosen() {
    this.revokeKioskImages()
    const files = Array.from(this.kioskInputTarget.files)
    files.forEach((file) => {
      const url = URL.createObjectURL(file)
      this.kioskUrls.push(url)
      const figure = document.createElement("span")
      figure.className = "brand-studio-kiosk-image is-new"
      figure.dataset.kioskNew = ""
      const img = document.createElement("img")
      img.src = url
      img.alt = "New kiosk start screen image"
      const badge = document.createElement("span")
      badge.className = "brand-studio-kiosk-remove"
      badge.textContent = "New"
      figure.append(img, badge)
      this.kioskGalleryTarget.appendChild(figure)
    })
    this.kioskHintTarget.textContent = files.length ? `${files.length} new - save to add ${files.length === 1 ? "it" : "them"}.` : ""
    this.kioskHintTarget.classList.toggle("d-none", !files.length)
  }

  revokeKioskImages() {
    this.kioskUrls.forEach((url) => URL.revokeObjectURL(url))
    this.kioskUrls = []
    if (this.hasKioskGalleryTarget) this.kioskGalleryTarget.querySelectorAll("[data-kiosk-new]").forEach((el) => el.remove())
  }

  showHeaderHint() {
    if (!this.headerUrl || !this.hasHeaderHintTarget) return

    this.headerHintTarget.textContent = this.menuOnGlobal() ? HEADER_HINTS.chosen : HEADER_HINTS.ownDesign
  }

  menuOnGlobal() {
    return this.hasMenuIdTarget && this.globalMenuIdsValue.includes(Number(this.menuIdTarget.value))
  }

  switchMenu(event) {
    const menuId = event.target.value
    this.menuIdTarget.value = menuId
    this.showMenuNote(menuId)
    this.showHeaderHint()
    this.reloadMenuFrame()
    if (this.hasDesignLinkTarget) {
      this.designLinkTarget.href = this.designLinkTarget.href.replace(/\/menus\/\d+\//, `/menus/${menuId}/`)
    }
    // The menu frame now shows another template, so its CSS has to be rebuilt.
    delete this.previewCss.menu
    if (this.dirty) this.refreshPreview()
  }

  // Navigation changes the menu's layout, not just its CSS, so the menu frame
  // is redrawn with the choice (menu_preview applies it, in memory, to a menu
  // without its own navigation - as a save would).
  navigationChanged(event) {
    this.navigationType = event.target.value
    this.setStatus("Previewing unsaved changes")
    // Each navigation has its own CSS, so a pending preview built for the old
    // one is dropped and rebuilt for the new one.
    delete this.previewCss.menu
    this.reloadMenuFrame()
    if (this.dirty) this.refreshPreview()
  }

  reloadMenuFrame() {
    if (!this.hasMenuFrameTarget) return

    const url = new URL(this.menuPreviewUrlValue, window.location.origin)
    url.searchParams.set("menu_id", this.menuIdTarget.value)
    if (this.navigationType) url.searchParams.set("navigation_type", this.navigationType)
    this.menuFrameTarget.src = url.toString()
  }

  // A frame that (re)loads draws its saved CSS; put the pending preview back.
  frameLoaded(event) {
    const kind = event.target === (this.hasMenuFrameTarget && this.menuFrameTarget) ? "menu" : "landing"
    this.applyCss(kind, event.target)
    this.applyLogo(kind, event.target)
    if (kind === "landing") this.applyBackground(event.target)
    if (kind === "menu") this.applyHeader(event.target)
  }

  async refreshPreview() {
    this.dirty = true
    const seq = ++this.requestSeq
    try {
      const response = await request(this.previewUrlValue, { method: "POST", body: this.brandFormData(), csrfToken: this.csrfTokenValue })
      if (seq !== this.requestSeq) return

      const data = await response.json()
      if (!response.ok) {
        this.setStatus((data.errors || ["That value can't be previewed"]).join(". "))
        return
      }

      this.previewCss = { landing: data.landing_css, menu: data.menu_css }
      this.frames().forEach(([kind, frame]) => this.applyCss(kind, frame))
      this.setStatus("Previewing unsaved changes - save to apply them")
    } catch (error) {
      if (seq === this.requestSeq) this.setStatus("The preview couldn't be updated")
    }
  }

  async applyToAll() {
    this.setStatus("Checking your menus…")
    let data
    try {
      const response = await request(this.clashesUrlValue, { method: "POST", body: this.brandFormData(), csrfToken: this.csrfTokenValue })
      data = await response.json()
      if (!response.ok) {
        this.setStatus((data.errors || ["Your brand can't be applied yet"]).join(". "))
        return
      }
    } catch (error) {
      this.setStatus("Your menus couldn't be checked - please try again")
      return
    }

    this.setStatus("")
    const navigation = data.navigation || []
    if (data.clashes.length === 0 && navigation.length === 0) return this.submitApplyToAll()

    const items = data.clashes.map((clash) => [clash.menus.join(", ") || clash.name, this.changeSummary(clash.changes)])
    if (navigation.length > 0) items.push([navigation.join(", "), `own navigation - switches to ${data.navigation_label}`])

    this.openConfirm({
      title: "Replace your menus' own styling?",
      intro: "These menus were changed in the advanced editor. Applying your brand to all menus replaces those changes:",
      items: items,
      button: "Save and replace",
      onConfirm: () => this.submitApplyToAll()
    })
  }

  confirmReset(event) {
    const form = event.currentTarget
    if (form.dataset.confirmed) return

    event.preventDefault()
    const clash = event.params.clash
    const menus = this.sentence(clash.menus)
    this.openConfirm({
      title: "Override with brand?",
      intro: `${menus || "These menus"} will go back to your brand styling, losing their own:`,
      items: clash.changes.map((change) => [change, ""]),
      button: "Override with brand",
      onConfirm: () => {
        form.dataset.confirmed = "true"
        form.requestSubmit()
      }
    })
  }

  // Why the previewed menu may not show the brand (its own theme, or its own
  // advanced-editor styling), and - for the latter - an override for its design.
  showMenuNote(menuId) {
    if (!this.hasMenuNoteTarget) return

    const note = this.menuNotesValue[menuId]
    this.menuNoteTarget.hidden = !note
    if (!note) return

    this.menuNoteTextTarget.textContent = note.text
    const form = this.menuNoteFormTarget
    form.hidden = !note.clash
    if (!note.clash) return

    form.querySelector('[name="template_id"]').value = note.clash.template_id
    form.querySelector('[name="menu_id"]').value = menuId
    form.dataset.brandStudioClashParam = JSON.stringify(note.clash)
    delete form.dataset.confirmed
  }

  confirm() {
    const onConfirm = this.pendingConfirm
    this.pendingConfirm = null
    this.modal().modal("hide")
    if (onConfirm) onConfirm()
  }

  openConfirm({ title, intro, items, button, onConfirm }) {
    this.confirmTitleTarget.textContent = title
    this.confirmIntroTarget.textContent = intro
    this.confirmListTarget.replaceChildren(...items.map(([label, detail]) => {
      const item = document.createElement("li")
      const strong = document.createElement("strong")
      strong.textContent = label
      item.append(strong)
      if (detail) item.append(` - ${detail}`)
      return item
    }))
    this.confirmButtonTarget.textContent = button
    this.pendingConfirm = onConfirm
    this.modal().modal("show")
  }

  submitApplyToAll() {
    this.applyToAllTarget.value = "1"
    this.formTarget.requestSubmit()
  }

  // The app runs Bootstrap 4.6, whose modals are jQuery plugins.
  modal() {
    return window.jQuery(this.confirmModalTarget)
  }

  // A design can have its own colours on dozens of parts; listing them all
  // pushes the confirm button out of view, so a long list becomes a count.
  changeSummary(changes) {
    if (changes.length <= 3) return `own ${this.sentence(changes)}`
    return `own colours on ${changes.length} parts`
  }

  sentence(words) {
    if (words.length <= 1) return words.join("")
    return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`
  }

  // The form as the server should judge it: the images are previewed locally and
  // _method would turn the POST into the form's PATCH.
  brandFormData() {
    const body = new FormData(this.formTarget)
    body.delete("restaurant_brand[logo]")
    body.delete("restaurant_brand[landing_background]")
    body.delete("menu_header_image")
    body.delete("kiosk_images[]")
    body.delete("_method")
    return body
  }

  applyCss(kind, frame) {
    const css = this.previewCss[kind]
    const style = css == null ? null : this.frameDocument(frame)?.getElementById(STYLE_IDS[kind])
    if (style) style.innerHTML = css
  }

  applyLogo(kind, frame) {
    const doc = this.frameDocument(frame)
    if (!doc || !this.logoUrl) return

    let style = doc.getElementById(LOGO_STYLE_ID)
    if (!style) {
      style = doc.createElement("style")
      style.id = LOGO_STYLE_ID
      doc.head.appendChild(style)
    }
    style.textContent = `${LOGO_TARGETS[kind]} { background-image: url("${this.logoUrl}") !important; }`
  }

  applyBackground(frame) {
    const doc = this.frameDocument(frame)
    if (!doc || !this.background) return

    let style = doc.getElementById(BACKGROUND_STYLE_ID)
    if (!style) {
      style = doc.createElement("style")
      style.id = BACKGROUND_STYLE_ID
      doc.head.appendChild(style)
    }
    style.textContent = this.background === "none"
      ? `${BACKGROUND_PAGE}, ${BACKGROUND_BOX} { background-image: none !important; }`
      : `${BACKGROUND_PAGE} { background-image: url("${this.background}") !important; background-size: cover !important; background-position: center !important; background-repeat: no-repeat !important; min-height: 100vh !important; }
         ${BACKGROUND_BOX} { background-image: none !important; background-color: transparent !important; }`
  }

  applyHeader(frame) {
    const doc = this.frameDocument(frame)
    if (!doc || !this.headerUrl || !this.menuOnGlobal()) return

    let style = doc.getElementById(HEADER_STYLE_ID)
    if (!style) {
      style = doc.createElement("style")
      style.id = HEADER_STYLE_ID
      doc.head.appendChild(style)
    }
    style.textContent = `${HEADER_TARGET} { background-image: url("${this.headerUrl}") !important; background-size: cover !important; background-position: center !important; background-repeat: no-repeat !important; }`
  }

  frames() {
    const frames = []
    if (this.hasLandingFrameTarget) frames.push(["landing", this.landingFrameTarget])
    if (this.hasMenuFrameTarget) frames.push(["menu", this.menuFrameTarget])
    return frames
  }

  frameDocument(frame) {
    try {
      return frame.contentDocument
    } catch (error) {
      return null
    }
  }

  hexInput(field) {
    return this.formTarget.querySelector(`[data-brand-studio-hex="${field}"]`)
  }

  picker(field) {
    return this.formTarget.querySelector(`[data-brand-studio-picker="${field}"]`)
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }
}
